import { useCallback, useContext, useEffect, useState } from "react";
import Joyride from "react-joyride";
import { getTutorialState } from "../../services/tutorialService";
import { updateTutorialState } from "../../services/tutorialService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { AuthContext } from "../../contexts";
import { GetTutorialStateResponse, TutorialFeature } from "../../proto/api_pb";
import { TutorialProgressState, TutorialState } from "../../proto/api_pb";
import Mixpanel from "../../utils/mixpanel";
import { RpcError } from "grpc-web";
import { joyrideStyles } from "./styles";
import { TrackObj } from "../../pages/MyTracks/types";
import { getOnboardingTutorialStepsForSmallScreen } from "./OnboardingTutorialSteps";
import { getOnboardingTutorialStepsForDesktop } from "./OnboardingTutorialSteps";

interface OnboardingTutorialProps {
  tracks: TrackObj[];
}

export const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({
  tracks,
}): JSX.Element => {
  const [steps, setSteps] = useState<any>();
  const [tutorialState, setTutorialState] = useState<TutorialState | undefined>(
    undefined
  );
  const [run, setRun] = useState(false);
  const { userId } = useContext(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();

  const handleJoyrideCallback = async (data: any) => {
    const accessToken = await getValidAccessToken();
    const { status, index } = data;
    Mixpanel.track(`onboarding_tutorial_${index}_${status}`);
    if (status === "running" && tutorialState && index === 0) {
      await updateTutorialState(
        accessToken,
        userId,
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_STARTED,
        TutorialFeature.TUTORIAL_FEATURE_INITIAL
      );
    } else if (status === "running" && tutorialState && index === 1) {
      await updateTutorialState(
        accessToken,
        userId,
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_PARTIAL,
        TutorialFeature.TUTORIAL_FEATURE_INITIAL
      );
    } else if (status === "finished" && tutorialState && index) {
      await updateTutorialState(
        accessToken,
        userId,
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_COMPLETE,
        TutorialFeature.TUTORIAL_FEATURE_INITIAL
      );
    }
  };

  const fetchAndSetTutorialState = useCallback(async () => {
    if (userId && userId.length > 0) {
      const accessToken = await getValidAccessToken();
      const response = await getTutorialState(
        accessToken,
        userId,
        TutorialFeature.TUTORIAL_FEATURE_INITIAL
      );
      if (response instanceof GetTutorialStateResponse) {
        setTutorialState(response.getTutorialState());
      } else if (response instanceof RpcError) {
        if (response.code === 5) {
          setTutorialState(new TutorialState());
        }
      }
    }
  }, [getValidAccessToken, userId]);

  useEffect(() => {
    fetchAndSetTutorialState();
  }, [fetchAndSetTutorialState]);

  useEffect(() => {
    if (!tutorialState) {
      return;
    }
    if (
      (tutorialState.getTutorialFeature() ===
        TutorialFeature.TUTORIAL_FEATURE_INITIAL ||
        tutorialState.getTutorialFeature() ===
          TutorialFeature.TUTORIAL_FEATURE_UNSPECIFIED) &&
      tutorialState.getTutorialProgressState() !==
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_COMPLETE
    ) {
      setRun(true);
      if (window.innerWidth > 700) {
        setSteps(getOnboardingTutorialStepsForDesktop(tracks));
      } else {
        setSteps(getOnboardingTutorialStepsForSmallScreen(tracks));
      }
    }
  }, [tracks, tutorialState]);

  return (
    <>
      <Joyride
        continuous
        callback={handleJoyrideCallback}
        run={run}
        steps={steps}
        hideCloseButton
        scrollToFirstStep
        showProgress
        disableScrolling
        styles={joyrideStyles}
      />
    </>
  );
};
